import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Check, X, Sparkles, Users, RefreshCw, Lock, Heart, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { API, authHeaders, fmtErr, mediaUrl } from '../../lib/auth';

const ProfileCard = ({ c, onConnect, onRespond }) => {
  const photos = c.photos && c.photos.length > 0 ? c.photos : (c.photo_url ? [c.photo_url] : []);
  const [idx, setIdx] = useState(0);
  const shift = (d) => setIdx((i) => (i + d + photos.length) % photos.length);
  return (
  <div className="pcard" data-testid={`pcard-${c.krysha_id}`}>
    <div className="pcard-top">
      {photos.length > 0 ? (
        <img className="pcard-photo" src={mediaUrl(photos[idx])} alt={c.first_name} data-testid={`pcard-photo-${c.krysha_id}`} />
      ) : (
        <div className="pcard-photo pcard-photo-empty">{c.first_name?.[0]?.toUpperCase() || '?'}</div>
      )}
      {photos.length > 1 && (
        <>
          <button type="button" className="pcard-nav left" onClick={() => shift(-1)} data-testid={`pcard-prev-${c.krysha_id}`}><ChevronLeft size={15} /></button>
          <button type="button" className="pcard-nav right" onClick={() => shift(1)} data-testid={`pcard-next-${c.krysha_id}`}><ChevronRight size={15} /></button>
          <div className="pcard-dots">{photos.map((_, i) => <span key={i} className={i === idx ? 'on' : ''} />)}</div>
        </>
      )}
      {c.score != null && <span className="score-badge" data-testid={`score-${c.krysha_id}`}>{c.score}% match</span>}
    </div>
    <div className="pcard-body">
      <b>{c.first_name}</b>
      <div className="pcard-meta">{[c.age && `${c.age} yrs`, c.city, c.personality].filter(Boolean).join(' · ') || 'Details coming soon'}</div>
      {c.headline && <div className="pcard-headline">“{c.headline}”</div>}
      {c.about && <p className="pcard-about">{c.about}</p>}
      {c.interests?.length > 0 && (
        <div className="ttags" style={{ marginTop: 8 }}>{c.interests.slice(0, 5).map((i) => <span className="ttag" key={i}>{i}</span>)}</div>
      )}
      <div className="pcard-actions">
        {c.connection_status === 'none' && (
          <button className="btn btn-red btn-sm" onClick={() => onConnect(c)} data-testid={`connect-btn-${c.krysha_id}`}><Send size={13} /> Send Connect Request</button>
        )}
        {c.connection_status === 'pending' && c.connection_direction === 'sent' && (
          <span className="pay-badge badge-muted" data-testid={`status-${c.krysha_id}`}>Request sent</span>
        )}
        {c.connection_status === 'pending' && c.connection_direction === 'received' && (
          <>
            <button className="btn btn-red btn-sm" onClick={() => onRespond(c.connection_id, 'accept')} data-testid={`card-accept-${c.krysha_id}`}><Check size={13} /> Accept</button>
            <button className="btn btn-ghost btn-sm" onClick={() => onRespond(c.connection_id, 'decline')} data-testid={`card-decline-${c.krysha_id}`}><X size={13} /> Decline</button>
          </>
        )}
        {c.connection_status === 'accepted' && <span className="pay-badge" data-testid={`status-${c.krysha_id}`}>Connected</span>}
        {c.connection_status === 'declined' && <span className="pay-badge badge-muted">Declined</span>}
      </div>
      <div className="pcard-id">{c.krysha_id}</div>
    </div>
  </div>
  );
};

const MyConnectionCard = ({ c, onOpen, onMessage }) => {
  const photos = c.photos?.length > 0 ? c.photos : (c.photo_url ? [c.photo_url] : []);
  return (
    <button type="button" className="pcard my-conn-card" onClick={() => onOpen(c)} data-testid={`myconn-${c.krysha_id}`}>
      <div className="pcard-top">
        {photos.length > 0 ? (
          <img className="pcard-photo" src={mediaUrl(photos[0], true)} alt={c.first_name} data-testid={`myconn-photo-${c.krysha_id}`} />
        ) : (
          <div className="pcard-photo pcard-photo-empty">{c.first_name?.[0]?.toUpperCase() || '?'}</div>
        )}
      </div>
      <div className="pcard-body">
        <b>{c.first_name}</b>
        <div className="pcard-meta">{[c.age && `${c.age} yrs`, c.city].filter(Boolean).join(' · ') || 'Tap to view profile'}</div>
        {c.headline && <div className="pcard-headline">“{c.headline}”</div>}
        <div className="pcard-actions">
          <span className="btn btn-red btn-sm" onClick={(e) => { e.stopPropagation(); onMessage(c); }} data-testid={`myconn-message-${c.krysha_id}`}><MessageCircle size={13} /> Message</span>
        </div>
        <div className="pcard-id">{c.krysha_id}</div>
      </div>
    </button>
  );
};

export const ConnectTab = ({ me, flash, go }) => {
  const [mode, setMode] = useState('matches');
  const [data, setData] = useState({ profiles: [], has_preferences: false });
  const [myConnections, setMyConnections] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback((m) => {
    setLoading(true);
    if (m === 'connections') {
      axios.get(`${API}/connections/mine`, { headers: authHeaders() })
        .then(({ data: d }) => setMyConnections(d)).catch(() => {}).finally(() => setLoading(false));
      return;
    }
    axios.get(`${API}/discover?mode=${m}`, { headers: authHeaders() })
      .then(({ data: d }) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(mode); }, [mode, load]);

  const goToChat = (c) => go(c.conversation_id ? `/inbox?conv=${c.conversation_id}` : '/inbox');
  const openProfile = (c) => go(`/member/${c.krysha_id}`, { state: { profile: c } });

  const connect = async (c) => {
    try {
      await axios.post(`${API}/connections/request`, { to_krysha_id: c.krysha_id }, { headers: authHeaders() });
      flash(`Connect request sent to ${c.first_name}. You'll see their reply in your Inbox.`);
      load(mode);
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  const respond = async (id, action) => {
    try {
      const { data: r } = await axios.post(`${API}/connections/${id}/respond`, { action }, { headers: authHeaders() });
      flash(r.conversation_id ? 'Connection accepted — chat unlocked in your Inbox.' : 'Request declined.');
      load(mode);
    } catch (err) { flash(fmtErr(err.response?.data?.detail)); }
  };

  return (
    <div style={{ display: 'grid', gap: 18 }} data-testid="connect-section">
      {!me.profile_live && (
        <div className="live-card" data-testid="connect-live-prompt">
          <div>
            <h3 style={{ margin: 0 }}>You're browsing privately</h3>
            <p>Your own profile is Private, so other members can't discover you yet. Make it live from My Profile to be seen too.</p>
          </div>
          <div className="live-actions"><button className="btn btn-red btn-sm" onClick={() => go('/profile')} data-testid="connect-goto-profile">Make My Profile Live</button></div>
        </div>
      )}

      <div className="connect-head">
        <div className="seg">
          <button className={mode === 'matches' ? 'on' : ''} onClick={() => setMode('matches')} data-testid="mode-matches"><Sparkles size={13} /> Best Matches For You</button>
          <button className={mode === 'all' ? 'on' : ''} onClick={() => setMode('all')} data-testid="mode-all"><Users size={13} /> All Live Profiles</button>
          <button className={mode === 'connections' ? 'on' : ''} onClick={() => setMode('connections')} data-testid="mode-connections"><Heart size={13} /> My Connections{myConnections.length > 0 && <span className="dash-badge">{myConnections.length}</span>}</button>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => load(mode)} data-testid="discover-refresh"><RefreshCw size={13} /> Refresh</button>
      </div>

      {mode === 'matches' && !data.has_preferences && (
        <div className="al-info" style={{ margin: 0 }} data-testid="no-prefs-note">
          Set your Match Preferences and Krysha will line up the profiles that fit your vibe. <button className="link-btn" onClick={() => go('/match-preferences')} data-testid="goto-prefs-btn">Set preferences</button>
        </div>
      )}

      {loading ? <p style={{ color: 'var(--muted)', fontSize: 13 }}>{mode === 'connections' ? 'Loading your connections…' : 'Finding profiles that match your vibe…'}</p> : mode === 'connections' ? (
        myConnections.length === 0 ? (
          <div className="dash-card" style={{ textAlign: 'center', padding: 40 }} data-testid="no-connections">
            <Heart size={30} style={{ color: 'var(--rosepink)' }} />
            <h3 style={{ marginTop: 12 }}>No connections yet</h3>
            <p style={{ color: 'var(--muted)', fontSize: 13.5, maxWidth: 440, margin: '0 auto' }}>
              Once someone accepts your connect request (or you accept theirs), they'll show up here with their full profile.
            </p>
          </div>
        ) : (
          <div className="pcard-grid" data-testid="my-connections-grid">
            {myConnections.map((c) => <MyConnectionCard key={c.krysha_id} c={c} onOpen={openProfile} onMessage={goToChat} />)}
          </div>
        )
      ) : data.profiles.length === 0 ? (
        <div className="dash-card" style={{ textAlign: 'center', padding: 40 }} data-testid="no-profiles">
          <Heart size={30} style={{ color: 'var(--rosepink)' }} />
          <h3 style={{ marginTop: 12 }}>{mode === 'matches' ? 'No matching live profiles yet' : 'No live profiles yet'}</h3>
          <p style={{ color: 'var(--muted)', fontSize: 13.5, maxWidth: 440, margin: '0 auto' }}>
            {mode === 'matches'
              ? 'Try “All Live Profiles”, or widen your Match Preferences a little — new members go live every week.'
              : 'Members are still completing their profiles. Check back soon — or fill your match form and let our team find someone for you.'}
          </p>
        </div>
      ) : (
        <div className="pcard-grid" data-testid="profiles-grid">
          {data.profiles.map((c) => <ProfileCard key={c.krysha_id} c={c} onConnect={connect} onRespond={respond} />)}
        </div>
      )}

      <p className="dash-privacy" style={{ justifyContent: 'center' }}>
        <Lock size={13} /> No swiping here. Email, mobile and WhatsApp numbers are never shown — they're only shared once both members accept a connection.
      </p>
    </div>
  );
};

export default ConnectTab;
